import React from "react";
import "./CaseStudy.css";
import TPShero from '../assets/images/futuraTPS-hero.png';
import { useNavigate } from "react-router-dom";

export default function AsuProj() {
  const navigate = useNavigate();

  return (
    <div className="case-page">

      <button className="case-back" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.5 11.5L2.5 7.5M2.5 7.5L6.5 3.5M2.5 7.5H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to Projects
      </button>

      <div className="case-hero">
        <h1>Type Specimen <em>Persona App</em></h1>
        <p className="case-hero-intro">
          An interactive Figma prototype reimagining the traditional type specimen poster as a dynamic, persona-driven font exploration tool — bringing the Futura typeface to life through motion, custom text input, and a fully navigable mobile experience.
        </p>
        <div className="case-meta-inline">
          <span><strong>Role</strong>UX Designer</span>
          <span><strong>Tools</strong>Figma (Variables, Auto Layout)</span>
          <span><strong>Type</strong>Mobile Prototype</span>
          <span><strong>Context</strong>Arizona State University</span>
        </div>
      </div>

      <div className="case-hero-img has-image">
        <img src={TPShero} alt="Type Specimen Persona App — Futura Explorer" />
      </div>

      <div className="case-body">

        {/* ── 01 Overview ── */}
        <div className="case-section">
          <span className="case-section-num">01 — Overview</span>
          <h2>Project <em>Overview</em></h2>
          <p>The <strong>Type Specimen Persona App</strong> is a high-fidelity mobile application prototype. Designed in the spirit of innovation and with a focus on an experiential outcome, this app brings a chosen typeface to life by showcasing its character, quality, and spirit through interactive exploration.</p>
          <p>Developed as the final project for the <em>Cross Media Design Solutions (GIT 540)</em> course within my Master's in UX program at <strong>Arizona State University</strong>, this project embodies the persona of the <strong>Futura</strong> typeface. It allows users to dynamically explore individual letterforms, glyphs, and test custom text — all within a seamlessly navigable mobile environment.</p>

          <div className="case-callout">"What if the traditional type specimen poster — a static print artifact — became a dynamic, interactive, persona-driven tool for font exploration?"</div>
        </div>

        <div className="case-divider" />

        {/* ── 02 Role & Contributions ── */}
        <div className="case-section">
          <span className="case-section-num">02 — Role</span>
          <h2>My Role &amp; <em>Contributions</em></h2>
          <p>As the sole UX/UI Designer on this project, I was responsible for the end-to-end design and advanced prototyping. My contributions encompassed:</p>
          <ul>
            <li>In-depth analysis of the chosen Typeface Persona to inform all design decisions</li>
            <li>Designing the complete user interface for a dynamic font exploration experience</li>
            <li>Implementing realistic screen flows and microinteractions to ensure a feeling of a functioning, polished product</li>
            <li>Building a fully navigable prototype in Figma, ensuring no disconnected task flows and embodying the persona throughout</li>
          </ul>
          <a
            href="https://www.figma.com/proto/CXEAfnDdWpMeahh4fTQH5D/Elizabeth_Budhai_TypePersonaTPS?node-id=94-30289&t=ycA9JXEiMBgFyRgb-1"
            target="_blank"
            rel="noopener noreferrer"
            className="case-link-btn"
          >
            View the Prototype in Figma
          </a>
        </div>

        <div className="case-divider" />

        {/* ── 03 Challenge & Goals ── */}
        <div className="case-section">
          <span className="case-section-num">03 — Challenge</span>
          <h2>The Challenge <em>&amp; Goals</em></h2>
          <p>The core challenge was to transform the traditional type specimen into a vibrant, experiential outcome that truly matched the personality of my chosen typeface (<strong>Futura</strong>) and its designated persona. Traditional static posters fail to convey the dynamic nuances of a typeface in use.</p>
          <p>Drawing directly from the <strong>Futura Persona</strong>, the app aimed to address the need for interactive font exploration and customization. My key goals were to:</p>
          <ul>
            <li><strong>Embody the persona's character:</strong> Ensure every design and interaction choice reflected the unique spirit of the typeface</li>
            <li><strong>Showcase comprehensive character sets:</strong> Include Uppercase A–Z, Lowercase a–z, standard punctuation glyphs, and at least six additional chosen glyphs (totaling over 70 characters)</li>
            <li><strong>Enable dynamic font interaction:</strong> Allow users to flip between different character weights and test custom words/phrases in real-time</li>
            <li><strong>Create a navigable, motion-rich prototype:</strong> Deliver a prototype that felt like a cohesive, functional mobile application with engaging motion and interactions</li>
          </ul>
          <div className="case-img">
            <img src="/images/FuturaAdobe.png" alt="Futura typeface reference" />
          </div>
        </div>

        <div className="case-divider" />

        {/* ── 04 Design Process ── */}
        <div className="case-section">
          <span className="case-section-num">04 — Process</span>
          <h2>Design Process <em>&amp; Wireframes</em></h2>

          <h3>Persona-Driven Ideation</h3>
          <p>Every design decision, from typography choices to interaction patterns, was directly informed by the <em>Futura Persona's</em> characteristics, ensuring the app's overall "feel" resonated with its intended user. I developed a comprehensive design system that informed color palettes, typography, iconography, and interactive elements.</p>

          <div className="case-img">
            <img src="/images/tpsDesign.png" alt="Type Specimen App design system and wireframes" />
          </div>

          <h3>Smart Feature Combination</h3>
          <p>Following a key pro tip, I intelligently combined features, allowing users to toggle between uppercase and lowercase input within a single, integrated keyboard view — rather than relying on separate screens. Low-fidelity grayscale wireframes helped validate layout and interaction flow before moving into high-fidelity design.</p>
        </div>

        <div className="case-divider" />

        {/* ── 05 Solution & Figma Deep Dive ── */}
        <div className="case-section">
          <span className="case-section-num">05 — Solution</span>
          <h2>Figma UI &amp; <em>Prototyping Deep Dive</em></h2>
          <p>The <strong>Futura Explorer</strong> prototype is a testament to advanced UI design and sophisticated prototyping in Figma, meticulously crafted to embody the typeface's personality and provide a seamless user experience.</p>

          <h3>Comprehensive Character Grid</h3>
          <p>The app features a dynamic grid view showcasing all required character sets: Uppercase A–Z, Lowercase a–z, standard punctuation (! , &amp; : ' () " @ # % $), and additional glyphs such as (€ Ж ∞ ≈ Ф †). Users can select a character from the grid to learn more about it in a dedicated detail view.</p>

          <div className="case-img-row">
            <div className="case-img">
              <img src="/images/tpsWeight.png" alt="Font weight adjustment component" />
            </div>
            <div className="case-img">
              <img src="/images/tpsText.png" alt="Interactive custom text input" />
            </div>
          </div>

          <h3>Dynamic Font Weight Adjustment</h3>
          <p>A custom-designed <code>FontWeightDropdown</code> component lets users switch weights. It utilizes Component Variants for Closed and Open states, Auto Layout ensures that content beneath the dropdown shifts downward when expanded, and a global <code>selectedFontWeight</code> variable is used across components to synchronize changes.</p>

          <h3>Interactive Custom Text Input</h3>
          <p>Users can test custom words or phrases using an interactive on-screen keyboard. Features include: dynamic typing, Clear Text, and uppercase/lowercase toggling. Keyboard keys and glyph display dynamically update based on <code>selectedFontWeight</code>, thanks to Variant mappings.</p>

          <h3>Figma Technical Process</h3>
          <p>This project demonstrates advanced Figma skill in:</p>
          <ul>
            <li><strong>Nested Component Architecture:</strong> Modular, scalable structure for components like keys</li>
            <li><strong>Component Variants:</strong> Used across interactive states</li>
            <li><strong>Figma Variables:</strong> Manage live data like <code>typedText</code>, <code>caseState</code>, and <code>selectedFontWeight</code></li>
            <li><strong>Auto Layout:</strong> For responsiveness and spacing consistency</li>
            <li><strong>Smart Animate:</strong> Adds polish and realistic motion</li>
            <li><strong>Complete Prototyping:</strong> Every screen is linked for a seamless experience</li>
          </ul>
        </div>

        <div className="case-divider" />

        {/* ── 06 Outcomes ── */}
        <div className="case-section">
          <span className="case-section-num">06 — Outcomes</span>
          <h2>Outcomes <em>&amp; Learnings</em></h2>
          <p>The <strong>Futura Explorer</strong> prototype successfully achieves the assignment's goal: an experiential outcome that intimately showcases a typeface's character. It feels like a functioning product, not just a demo — fully embodying the chosen persona through its design and interaction.</p>

          <h3>Key Takeaways</h3>
          <ul>
            <li>
              <strong>Advanced Figma Prototyping:</strong> Mastery of complex variables, component variants, and dynamic layout strategies
            </li>
            <li>
              <strong>User-Centered Design:</strong> Translating a persona's traits into tangible UI and interaction patterns
            </li>
            <li>
              <strong>Systems Thinking:</strong> Creating a design system where one change cascades meaningfully across the interface
            </li>
          </ul>
          <p>This project pushed the boundaries of what's possible in prototyping and showcased how a typeface can become an interactive story.</p>
        </div>

        <div className="case-divider" />

        {/* ── 07 At a Glance ── */}
        <div className="case-section">
          <span className="case-section-num">07 — At a Glance</span>
          <h2>Project <em>Snapshot</em></h2>
          <div className="outcome-grid">
            <div className="outcome-cell">
              <div className="outcome-number">3</div>
              <div className="outcome-desc">Advanced Figma features used: Variables, Component Variables, Auto Layout</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">∞</div>
              <div className="outcome-desc">Custom user text inputs supported in real-time</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">100%</div>
              <div className="outcome-desc">Fully navigable interactive prototype, no disconnected hotspots</div>
            </div>
            <div className="outcome-cell">
              <div className="outcome-number">UX</div>
              <div className="outcome-desc">Reimagined a traditional print artifact as a digital interaction</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
